import { useState, useEffect } from 'react';
import { log } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/form/label';
import { Switch } from '@/components/ui/form/switch';
import { Bell, Save, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { fetchNotificationPreferences, updateNotificationPreferences } from '@/lib/api';

interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  workoutReminders: boolean;
  weeklySummary: boolean;
  goalAchievements: boolean;
  coachMessages: boolean;
  trainingLoadAlerts: boolean;
  raceReminders: boolean;
}

const notificationGroups = [
  {
    title: 'GENERAL',
    items: [
      { key: 'emailNotifications' as const, label: 'Email Notifications', desc: 'Receive notifications via email' },
      { key: 'pushNotifications' as const, label: 'Push Notifications (Coming Soon)', desc: 'Receive push notifications on your device' },
    ],
  },
  {
    title: 'TRAINING',
    items: [
      { key: 'workoutReminders' as const, label: 'Workout Reminders', desc: 'Get reminded about upcoming workouts' },
      { key: 'trainingLoadAlerts' as const, label: 'Training Load Alerts', desc: 'Alerts when training load is high or low' },
      { key: 'raceReminders' as const, label: 'Race Reminders', desc: 'Reminders about upcoming races and events' },
    ],
  },
  {
    title: 'UPDATES & REPORTS',
    items: [
      { key: 'weeklySummary' as const, label: 'Weekly Summary', desc: 'Receive a weekly training summary' },
      { key: 'goalAchievements' as const, label: 'Goal Achievements', desc: 'Notifications when you achieve training goals' },
      { key: 'coachMessages' as const, label: 'Coach Messages', desc: 'Notifications when the coach sends messages' },
    ],
  },
];

export function NotificationsSection() {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    emailNotifications: true,
    pushNotifications: true,
    workoutReminders: true,
    weeklySummary: true,
    goalAchievements: true,
    coachMessages: true,
    trainingLoadAlerts: true,
    raceReminders: true,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialPreferences, setInitialPreferences] = useState<NotificationPreferences | null>(null);

  useEffect(() => { loadPreferences(); }, []);

  useEffect(() => {
    if (initialPreferences) {
      setHasChanges(JSON.stringify(preferences) !== JSON.stringify(initialPreferences));
    } else {
      setHasChanges(false);
    }
  }, [preferences, initialPreferences]);

  const loadPreferences = async () => {
    setIsLoading(true);
    try {
      const prefs = await fetchNotificationPreferences();
      const frontendPrefs: NotificationPreferences = {
        emailNotifications: prefs.email_notifications,
        pushNotifications: prefs.push_notifications,
        workoutReminders: prefs.workout_reminders,
        trainingLoadAlerts: prefs.training_load_alerts,
        raceReminders: prefs.race_reminders,
        weeklySummary: prefs.weekly_summary,
        goalAchievements: prefs.goal_achievements,
        coachMessages: prefs.coach_messages,
      };
      setPreferences(frontendPrefs);
      setInitialPreferences(frontendPrefs);
    } catch (error) {
      log.error('NOTIFICATIONS', 'Failed to load notification preferences:', error);
      toast({ title: 'Failed to load preferences', description: error instanceof Error ? error.message : 'Could not load your notification preferences', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateNotificationPreferences({
        email_notifications: preferences.emailNotifications,
        push_notifications: preferences.pushNotifications,
        workout_reminders: preferences.workoutReminders,
        training_load_alerts: preferences.trainingLoadAlerts,
        race_reminders: preferences.raceReminders,
        weekly_summary: preferences.weeklySummary,
        goal_achievements: preferences.goalAchievements,
        coach_messages: preferences.coachMessages,
      });
      setInitialPreferences({ ...preferences });
      setHasChanges(false);
      toast({ title: 'Notification preferences updated', description: 'Your notification settings have been saved' });
    } catch (error) {
      log.error('NOTIFICATIONS', 'Failed to save preferences:', error);
      toast({ title: 'Failed to save preferences', description: error instanceof Error ? error.message : 'Could not save your notification settings', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const updatePreference = (key: keyof NotificationPreferences, value: boolean) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-6">
        <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
          <Bell className="h-4 w-4 text-[#00FFC8]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">Notifications</h3>
          <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
            ALERT CONFIGURATION
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-5">
          {notificationGroups.map((group) => (
            <div key={group.title}>
              <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-white/30 mb-3 block">
                {group.title}
              </span>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center justify-between px-3 py-2.5 rounded-md border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="space-y-0.5">
                      <Label htmlFor={item.key} className="cursor-pointer text-sm text-foreground">
                        {item.label}
                      </Label>
                      <p className="text-[10px] font-mono text-[#A0A0A0]">{item.desc}</p>
                    </div>
                    <Switch
                      id={item.key}
                      checked={preferences[item.key]}
                      onCheckedChange={(checked) => updatePreference(item.key, checked)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Save */}
          <div className="pt-2">
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200 border',
                hasChanges
                  ? 'border-[rgba(0,255,200,0.3)] bg-[rgba(0,255,200,0.08)] text-[#00FFC8] hover:bg-[rgba(0,255,200,0.15)] hover:shadow-[0_0_16px_rgba(0,255,200,0.15)]'
                  : 'border-white/[0.06] bg-white/[0.02] text-white/30 cursor-not-allowed'
              )}
            >
              {isSaving ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> COMMITTING...</>
              ) : (
                <><Save className="h-3.5 w-3.5" /> COMMIT CHANGES</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
